import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, StatusBadge } from '@/components/common/Badge';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { ListPage } from '@/components/tables/ListPage';
import { SelectField, TextField } from '@/components/forms/Field';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { employeesApi, teachersApi } from '@/services/api/endpoints/staff';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { Teacher } from '@/types/domain';
import { orDash } from '@/utils/format';

/**
 * Teachers — the teaching facts about an employee.
 *
 * Employment number, joining date and status live on the employee and are shown
 * here read-only, so ending someone's employment is one change in one place
 * rather than two that can disagree.
 */
export function TeachersPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('createdAt,asc');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Teacher | null>(null);
  const [form, setForm] = useState({
    employeeId: '',
    specialization: '',
    qualificationSummary: '',
    yearsOfExperience: '',
    maxWeeklyPeriods: '',
  });

  const query = useQuery({
    queryKey: queryKeys.teachers.list(table.params),
    queryFn: () => teachersApi.list(table.params),
  });

  /* Only employees who are not already teachers can be registered, since the
     backend permits one teacher record per employee. */
  const employees = useQuery({
    queryKey: queryKeys.employees.list({ size: 200, status: 'ACTIVE' }),
    queryFn: () => employeesApi.list({ page: 0, size: 200, status: 'ACTIVE' }),
    enabled: formOpen && can(PERMISSIONS.EMPLOYEE_VIEW),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.teachers.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.employees.all });
  };

  const asNumber = (value: string) => (value === '' ? undefined : Number(value));

  const payload = () => ({
    employeeId: editing?.employeeId ?? form.employeeId,
    specialization: form.specialization === '' ? undefined : form.specialization,
    qualificationSummary: form.qualificationSummary === '' ? undefined : form.qualificationSummary,
    yearsOfExperience: asNumber(form.yearsOfExperience),
    maxWeeklyPeriods: asNumber(form.maxWeeklyPeriods),
  });

  const save = useMutation({
    mutationFn: () =>
      editing === null ? teachersApi.create(payload()) : teachersApi.update(editing.id, payload()),
    onSuccess: () => {
      toast.success(editing === null ? 'Teacher registered' : 'Teacher updated');
      setFormOpen(false);
      setEditing(null);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const openEdit = (teacher: Teacher) => {
    setEditing(teacher);
    setForm({
      employeeId: teacher.employeeId,
      specialization: teacher.specialization ?? '',
      qualificationSummary: teacher.qualificationSummary ?? '',
      yearsOfExperience: teacher.yearsOfExperience?.toString() ?? '',
      maxWeeklyPeriods: teacher.maxWeeklyPeriods?.toString() ?? '',
    });
    setFormOpen(true);
  };

  const columns: Column<Teacher>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (teacher) => (
        <div>
          <div className="font-medium text-ink">{orDash(teacher.fullName)}</div>
          <div className="font-mono text-xs text-subtle">{teacher.employeeNumber}</div>
        </div>
      ),
    },
    {
      key: 'specialization',
      header: 'Specialisation',
      sortKey: 'specialization',
      render: (teacher) => orDash(teacher.specialization),
    },
    {
      key: 'experience',
      header: 'Experience',
      sortKey: 'yearsOfExperience',
      render: (teacher) =>
        teacher.yearsOfExperience === undefined ? '—' : `${teacher.yearsOfExperience} yrs`,
    },
    {
      key: 'load',
      header: 'Weekly ceiling',
      render: (teacher) =>
        teacher.maxWeeklyPeriods === undefined ? (
          <span className="text-subtle">Not set</span>
        ) : (
          <span>
            {teacher.maxWeeklyPeriods}
            <span className="ml-1 text-xs text-subtle">periods</span>
          </span>
        ),
    },
    {
      key: 'availability',
      header: 'Availability',
      render: (teacher) =>
        teacher.availableForAssignment ? (
          <Badge tone="success">Available</Badge>
        ) : (
          <StatusBadge status={teacher.employmentStatus} />
        ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (teacher) =>
        can(PERMISSIONS.TEACHER_UPDATE) ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={(event) => {
              event.stopPropagation();
              openEdit(teacher);
            }}
          >
            Edit
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      <ListPage
        title="Teachers"
        description="Employees who teach. Employment details are managed on the employee record."
        action={
          can(PERMISSIONS.TEACHER_CREATE) ? (
            <Button
              onClick={() => {
                setEditing(null);
                setForm({
                  employeeId: '',
                  specialization: '',
                  qualificationSummary: '',
                  yearsOfExperience: '',
                  maxWeeklyPeriods: '',
                });
                setFormOpen(true);
              }}
            >
              Register teacher
            </Button>
          ) : undefined
        }
        pagination={query.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={query.data?.data}
          rowKey={(teacher) => teacher.id}
          loading={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle="No teachers yet"
          emptyDescription="Register an employee as a teacher to assign them classes."
        />
      </ListPage>

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editing === null ? 'Register a teacher' : `Edit ${editing.fullName ?? 'teacher'}`}
        description={
          editing === null
            ? 'Choose an employee. Everything they share with non-teaching staff stays on their employment record.'
            : 'Teaching details only.'
        }
        footer={
          <>
            <Button variant="ghost" onClick={() => setFormOpen(false)} disabled={save.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => save.mutate()}
              loading={save.isPending}
              disabled={editing === null && form.employeeId === ''}
            >
              {editing === null ? 'Register' : 'Save changes'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {editing === null && (
            <SelectField
              label="Employee"
              required
              value={form.employeeId}
              onChange={(event) => setForm({ ...form, employeeId: event.target.value })}
            >
              <option value="">Select an employee…</option>
              {employees.data?.data
                .filter((employee) => !employee.teacher)
                .map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.fullName ?? employee.employeeNumber} · {employee.employeeNumber}
                  </option>
                ))}
            </SelectField>
          )}

          <TextField
            label="Specialisation"
            value={form.specialization}
            onChange={(event) => setForm({ ...form, specialization: event.target.value })}
            placeholder="e.g. Mathematics"
          />

          <TextField
            label="Qualifications"
            value={form.qualificationSummary}
            onChange={(event) => setForm({ ...form, qualificationSummary: event.target.value })}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Years of experience"
              type="number"
              min={0}
              max={70}
              value={form.yearsOfExperience}
              onChange={(event) => setForm({ ...form, yearsOfExperience: event.target.value })}
            />
            <TextField
              label="Weekly period ceiling"
              type="number"
              min={1}
              value={form.maxWeeklyPeriods}
              onChange={(event) => setForm({ ...form, maxWeeklyPeriods: event.target.value })}
              hint="Advisory — the timetable reports breaches rather than refusing them"
            />
          </div>
        </div>
      </Modal>
    </>
  );
}
